import React, { FC } from 'react';
import { SubtractUniversal } from '@duxton-web/icons';
import { Flex, Tag } from '@duxton-web/ui';
import { Tooltip } from 'antd';
import { capitalize } from 'lodash';

import { VariableState } from '../../../../constants';
import { Criticality } from '../../../../lib/PlannerDTOs';
import { isProduction } from '@/common/environment';
import { capitalizeFirstLetter } from '@/utils/string';

interface VariableStateTagProps {
  variable: any;
  className?: string;
}

export const VariableStateTagStyle = {
  [VariableState.ACTIVE]: {
    color: 'success',
  },
  [VariableState.INACTIVE]: {
    color: 'default',
    style: {
      color: '#A3A3A3',
    },
  },
  [VariableState.INUSE]: {
    color: 'blue',
  },
  [VariableState.ARCHIVED]: {
    color: 'default',
  },
  [VariableState.UNPUBLISHED]: {
    color: 'red',
  },
};

const VariableStateTag: FC<VariableStateTagProps> = ({ variable }) => {
  const renderTag = () => {
    const isVariableCritical = variable.criticality === Criticality.Critical;
    const states = [];

    switch (variable.state) {
      case VariableState.UNPUBLISHED:
        states.push(
          <Tooltip
            key={`${variable.name}-status-unpublished`}
            title="The variable has been created but not yet published. For your code to receive rollout values from GrabX, please set up your rollout targets, before clicking Validate and Rollout."
          >
            <Tag
              size="xsmall"
              color={VariableStateTagStyle[VariableState.UNPUBLISHED].color}
              bordered={false}
            >
              {capitalize(VariableState.UNPUBLISHED)}
            </Tag>
          </Tooltip>,
        );
        break;
      case VariableState.ACTIVE:
        {
          if (!isProduction) states.push(<SubtractUniversal />);
          else {
            states.push(
              <Tooltip
                key={`${variable.name}-status-active`}
                title="The variable has been triggered in the last 3 months."
              >
                <Tag
                  size="xsmall"
                  color={VariableStateTagStyle[VariableState.ACTIVE].color}
                  bordered={false}
                >
                  {capitalizeFirstLetter(VariableState.ACTIVE)}
                </Tag>
              </Tooltip>,
            );
          }
        }
        break;
      case VariableState.INACTIVE:
        {
          if (!isProduction) states.push(<SubtractUniversal />);
          else {
            states.push(
              <Tooltip
                key={`${variable.name}-status-inactive`}
                title="The variable has not been triggered in the last 3 months. Consider Archiving it if it is no longer in use."
              >
                <Tag
                  size="xsmall"
                  color={VariableStateTagStyle[VariableState.INACTIVE].color}
                  bordered={false}
                  style={VariableStateTagStyle[VariableState.INACTIVE].style}
                >
                  {capitalizeFirstLetter(VariableState.INACTIVE)}
                </Tag>
              </Tooltip>,
            );
          }
        }
        break;
      case VariableState.ARCHIVED:
        states.push(
          <Tag
            size="xsmall"
            color={VariableStateTagStyle[VariableState.ARCHIVED].color}
            bordered={false}
          >
            {capitalizeFirstLetter(VariableState.ARCHIVED)}
          </Tag>,
        );
        break;
      default:
        states.push(
          <Tag
            size="xsmall"
            color={VariableStateTagStyle[VariableState.INUSE].color}
            bordered={false}
          >
            Inuse
          </Tag>,
        );
        break;
    }

    if (isVariableCritical)
      states.push(
        <Tag size="xsmall" color="error" bordered={false}>
          Critical
        </Tag>,
      );

    return (
      <Flex gap={4} align="center" wrap="wrap">
        {states}
      </Flex>
    );
  };

  return renderTag();
};

export default VariableStateTag;
